// Reading a native <input type="date"> value ("yyyy-MM-dd", or "" when any
// segment is empty).
//
// Desktop Chrome and Firefox let people TYPE the date segment by segment, and
// fire a change for every intermediate value on the way: typing the year 2026
// reports 0002, 0020, 0202 and only then 2026. Clamping those to "tomorrow" on
// the spot overwrote the field mid-typing (8 Oct 2026 bug). So a value before
// the earliest bookable day is 'pending': the caller holds it and only
// corrects it once the field loses focus.

// 'clear' | 'pending' (before min, wait for blur) | 'ok'. `min` is the
// input's own min attribute, also "yyyy-MM-dd", so a string compare is exact.
export const classifyDateInput = (raw, min) => {
  if (!raw) return 'clear'
  return raw < min ? 'pending' : 'ok'
}

// Parse yyyy-MM-dd as a LOCAL date (not UTC) via the Date constructor's
// component form: avoids an off-by-one around midnight outside UTC.
export const parseDateInput = raw => {
  const [y, m, d] = raw.split('-').map(Number)
  return new Date(y, m - 1, d)
}

// ---- Measurement (ANALYTICS.md §2, "Booking date field") ----------------
// Pure, so the numbers the booking_date_* events carry are pinned by tests.
// No date text ever leaves the browser: only whole-day offsets from today.

// Calendar days from `now` to `date` in the browser's own zone (0 = today,
// negative = past). Rounded, so a DST day of 23 or 25 hours still counts as 1.
export const daysAhead = (date, now = new Date()) => {
  const a = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((a - b) / 86400000)
}

// Keys that change a date input's value from the keyboard: a digit typed into
// a segment, Backspace/Delete, ArrowUp/Down stepping a segment. Tab, Shift,
// Enter and ArrowLeft/Right only move around, so they do not make it 'typed'.
export const isDateEditKey = key =>
  typeof key === 'string' && (/^\d$/.test(key) || ['Backspace', 'Delete', 'ArrowUp', 'ArrowDown'].includes(key))

const inputMethod = typed => (typed ? 'typed' : 'picker')

// booking_date_set properties for the date that was applied.
export const dateSetProps = (date, typed, now = new Date()) => {
  const days = daysAhead(date, now)
  return { days_ahead: days, input_method: inputMethod(typed), same_day: days === 0 }
}

// booking_date_corrected properties when `requested` was moved to the
// earliest day: 'past' for a day before today, 'after_cutoff' for today once
// same-day booking has closed (from_days_ahead is then 0). A half-typed year
// ("0202") parses into the 1900s, so a huge negative number is that trap.
export const dateCorrectionProps = (requested, typed, now = new Date()) => {
  const days = daysAhead(requested, now)
  return { reason: days < 0 ? 'past' : 'after_cutoff', from_days_ahead: days, input_method: inputMethod(typed) }
}

// ---- Which day a pick lands on -------------------------------------------
// Pure, so the cutoff is pinned by tests in a London clock (BST and GMT).

// Midnight (the browser's zone) of the day `days` after `date`'s day.
export const startOfDayPlus = (date, days = 0) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

// The day Book & pay starts on: tomorrow, always. Today stays a choice the
// customer makes (it costs the same-day surcharge), never a default.
export const defaultBookingDate = (now = new Date()) => startOfDayPlus(now, 1)

// What a picked day becomes at `now`, same-day open or not:
// { date, sameDay, corrected }. Today while same-day is open stays today; a
// day before the earliest bookable one moves to tomorrow (corrected); any
// other day is kept.
//
// Compared as whole days. It used to compare the picked day (midnight) with
// addDays(now, 1), which is tomorrow at the current time of day, so picking
// tomorrow itself was "before the earliest day" and fired
// booking_date_corrected 'after_cutoff' on every pick of tomorrow, morning or
// not (seen at 07:59 London on 8 Oct 2026).
export const resolveBookingDate = (requested, now = new Date(), todayOpen = false) => {
  const day = startOfDayPlus(requested, 0)
  const today = startOfDayPlus(now, 0)
  if (todayOpen && day.getTime() === today.getTime()) return { date: day, sameDay: true, corrected: false }
  const earliest = startOfDayPlus(now, 1)
  if (day < earliest) return { date: earliest, sameDay: false, corrected: true }
  return { date: day, sameDay: false, corrected: false }
}
